// usePapers — single source for the user's paper list.
//
// - Anonymous: returns the bundled seed array (PAPERS) so the prototype
//   stays interactive without an account.
// - Logged-in: fetches from /api/papers and falls back to the seed only
//   while the first request is in-flight.
//
// Components that read papers should also consume `status` so they can show
// a spinner instead of flashing seed → real data. Mutations elsewhere
// (importPapers, setPaperStatus, …) should call invalidatePapersCache()
// then refresh() on the consuming hook.

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from './auth.jsx';

let _cache = null;          // last-fetched array per user_id (single-user app, so just one slot)
let _cacheUserId = null;
const _listeners = new Set();

export function invalidatePapersCache() {
  _cache = null;
  _cacheUserId = null;
  for (const fn of _listeners) {
    try { fn(); } catch {}
  }
}

export function usePapers() {
  const auth = useAuth();
  const seed = typeof window !== 'undefined' ? (window.PAPERS ?? []) : [];

  const [papers, setPapers] = useState(() =>
    auth.user && _cacheUserId === auth.user.id ? _cache : seed,
  );
  const [status, setStatus] = useState('idle');
  const [error, setError]   = useState(null);

  const fetchOnce = useCallback(async () => {
    if (!auth.user) {
      setPapers(seed);
      setStatus('ready');
      return;
    }
    setStatus('loading');
    try {
      const res = await fetch('/api/papers?limit=200', { credentials: 'same-origin' });
      const body = await res.json().catch(() => null);
      if (!res.ok || !body?.ok) throw new Error(body?.error?.message ?? `papers ${res.status}`);
      const items = body.data?.items ?? [];
      // Empty library → fall back to seed so the prototype still has content
      // to show. Anything the user has actually imported takes precedence.
      const effective = items.length > 0 ? items.map(adaptDbPaper) : seed;
      _cache = effective;
      _cacheUserId = auth.user.id;
      setPapers(effective);
      setStatus('ready');
    } catch (e) {
      setError(e);
      setStatus('error');
      setPapers(seed);
    }
  }, [auth.user, seed]);

  useEffect(() => { fetchOnce(); }, [fetchOnce]);

  // Re-fetch whenever someone else invalidates the cache (e.g. after an
  // import or status change on a different screen).
  useEffect(() => {
    _listeners.add(fetchOnce);
    return () => { _listeners.delete(fetchOnce); };
  }, [fetchOnce]);

  return { papers, status, error, refresh: fetchOnce };
}

// The /api/papers row uses arrays/objects already; we just normalise a few
// names so the screens (which were written against the seed shape) keep
// working without rewrites.
function adaptDbPaper(row) {
  const meta = row.metadata ?? {};
  return {
    id: row.id,
    title: row.title,
    authors: row.authors ?? [],
    venue: row.venue ?? meta.primary_category ?? '',
    date: meta.published ? meta.published.slice(0, 10).replaceAll('-', '.') : '',
    arxiv: row.arxiv_id ?? '',
    tags: row.tags ?? [],
    summary: row.summary ?? '',
    dataset: meta.dataset ?? '',
    code: meta.code ?? '',
    why: meta.why ?? '',
    worth: meta.worth ?? 0.7,
    novel: meta.novel ?? 0.7,
    impl: meta.impl ?? 0.6,
    must: meta.must ?? false,
    status: row.status ?? null,
    _db: true,
    _meta: meta,
  };
}
